// Spanish copy for /face-data-changes-2026 (hand-written from copy.en.ts; same shape, same facts).
import type { FaceDataCopy } from "./copy.en";

const es: FaceDataCopy = {
  seo: {
    title: "Datos faciales desde el 1 de noviembre | Photohawk",
    description:
      "Desde el 1 de noviembre de 2026, los datos faciales de la búsqueda por selfie se eliminan automáticamente 12 meses después de subir cada foto. Las fotos, las ventas, las descargas y la búsqueda por dorsal no cambian. Así funciona y por qué.",
  },
  hero: {
    badge: "Desde el 1 de noviembre de 2026",
    title: "Búsqueda por selfie y datos faciales:",
    titleHighlight: "qué cambia",
    subtitle:
      "Desde el 1 de noviembre, los datos faciales que hacen funcionar la búsqueda por selfie se eliminan automáticamente 12 meses después de subir cada foto. Tus fotos se quedan exactamente donde están. Las leyes de privacidad exigen un límite fijo como este, así que no es algo que podamos dejar de hacer.",
    ctaPolicy: "Leer la política de datos faciales",
    ctaHow: "Ver cómo funciona",
  },
  timeline: {
    title: "Cómo funciona",
    intro: "Cada foto sigue el mismo camino. El plazo es por foto: empieza cuando se sube esa foto.",
    label: "Línea de tiempo animada: se sube una foto, la búsqueda por selfie la encuentra durante 12 meses, luego se eliminan sus datos faciales y la foto sigue disponible.",
    caption:
      "Se sube una foto, la búsqueda por selfie la encuentra durante 12 meses y después sus datos faciales se eliminan automáticamente. La foto, sus ventas y la búsqueda por dorsal siguen igual.",
    steps: [
      { id: "upload", when: "Día 1", title: "Se sube una foto", body: "Las caras que aparecen se convierten en datos faciales, para que los compradores la encuentren con un selfie." },
      { id: "search", when: "Durante 12 meses", title: "La búsqueda por selfie la encuentra", body: "Los compradores se hacen un selfie y ven todas las fotos en las que aparecen, como hoy." },
      { id: "delete", when: "A los 12 meses", title: "Se eliminan sus datos faciales", body: "De forma automática y permanente. La foto no vuelve a indexarse para la búsqueda facial." },
      { id: "carry", when: "Después", title: "La foto sigue ahí", body: "Los compradores pueden encontrarla por dorsal, por hora o navegando, y comprarla y descargarla." },
    ],
    scene: {
      selfie: "Selfie",
      upload: "Subida",
      search: "Búsqueda selfie",
      months: "12 meses",
      deleted: "Datos eliminados",
      carry: "La foto sigue",
      match: "Coincide",
    },
  },
  dissolve: {
    title: "Los datos faciales no son la foto",
    intro:
      "Los datos faciales son un conjunto de números que describen las caras de una foto: una plantilla facial. Solo sirven para comparar el selfie de un comprador. Eliminarlos borra la plantilla y nada más.",
    label: "Ilustración animada: una foto con su plantilla facial al lado; la plantilla se desvanece mientras la foto, su dorsal y su venta se quedan.",
    caption: "La plantilla facial se desvanece. La foto, su dorsal y su venta no cambian.",
    scene: { photo: "Foto", template: "Plantilla facial", bib: "Dorsal 1234", sale: "Vendida", gone: "Eliminada" },
  },
  november: {
    title: "Qué pasa el 1 de noviembre",
    intro: "Las fotos subidas hace más de 12 meses ya han superado el límite, así que se tratan primero.",
    points: [
      {
        id: "backlog",
        title: "Las fotos más antiguas pierden primero sus datos faciales",
        body: "Los datos faciales de las fotos subidas hace más de 12 meses se eliminan desde el 1 de noviembre. Lo hacemos por tandas, así que puede tardar unos días en llegar a todas las galerías. Desde el 1 de noviembre no se crean datos faciales nuevos para esas fotos.",
      },
      {
        id: "daily",
        title: "Después, una revisión diaria",
        body: "A partir de entonces, un proceso automático se ejecuta cada día y elimina los datos faciales de cada foto cuando cumple 12 meses. No hay nada que configurar ni que recordar.",
      },
      {
        id: "ended",
        title: "Cuando toda una galería supera los 12 meses",
        body: "La búsqueda por selfie termina en esa galería. Los compradores que la prueben verán una nota breve que les indica la búsqueda por dorsal y la navegación.",
      },
      {
        id: "off",
        title: "Galerías cerradas o con la búsqueda por selfie desactivada",
        body: "Los datos faciales se eliminan 30 días después de cerrar una galería o desactivar su búsqueda por selfie. En las galerías que ya estaban cerradas o desactivadas antes del 1 de noviembre, esos 30 días empiezan el 1 de noviembre.",
      },
    ],
  },
  unchanged: {
    title: "Qué no cambia",
    intro: "Eliminar datos faciales nunca elimina una foto. Todo lo demás sigue como hoy:",
    items: [
      { id: "photos", text: "Tus fotos y tus galerías" },
      { id: "sales", text: "Las ventas, los pedidos y tus ingresos" },
      { id: "downloads", text: "Las descargas, también en las galerías de descarga gratuita" },
      { id: "bib", text: "La búsqueda por dorsal o número, y por texto" },
      { id: "time", text: "La búsqueda por hora y la navegación" },
      { id: "recent", text: "La búsqueda por selfie en las fotos subidas en los últimos 12 meses" },
    ],
  },
  buyers: {
    title: "Qué notarán tus compradores",
    intro: "La mayoría busca sus fotos en los días y semanas posteriores al evento, así que la mayoría no notará nada.",
    items: [
      { id: "older", text: "La búsqueda por selfie no encontrará fotos subidas hace más de 12 meses." },
      { id: "other", text: "Podrán seguir encontrándolas por dorsal, por hora o navegando por la galería, y comprarlas y descargarlas como siempre." },
      { id: "note", text: "En una galería en la que todas las fotos tienen más de 12 meses, la búsqueda por selfie muestra una nota breve con las otras formas de buscar." },
      { id: "selfies", text: "Los selfies que se usan para buscar siguen sin guardarse nunca, como hoy." },
    ],
    tip: "Un buen hábito: recuerda a los compradores que busquen poco después de cada evento, en tus correos o en redes sociales.",
  },
  why: {
    title: "Por qué ocurre esto",
    lead: "No es algo sobre lo que tengamos mucho control. Viene de las leyes de privacidad, no de una decisión de recortar la búsqueda por selfie.",
    paragraphs: [
      "Las plantillas faciales son datos biométricos, que las leyes de privacidad consideran de los datos personales más sensibles que existen. Esas leyes exigen que tengan un límite de conservación fijo y publicado, que no se guarden más tiempo del necesario y que después se eliminen automáticamente. Guardar datos faciales indefinidamente no es una opción para nadie que ofrezca búsqueda facial.",
      "Las galerías de Photohawk se usan en todo el mundo, así que una sola regla tiene que cumplir todas estas leyes a la vez:",
    ],
    laws: [
      { id: "gdpr", name: "RGPD y UK GDPR", text: "Los datos biométricos usados para identificar a personas son una categoría especial de datos personales, y el principio de limitación del plazo de conservación exige no guardarlos más tiempo del necesario." },
      { id: "bipa", name: "Illinois (BIPA)", text: "Exige un calendario de conservación público y por escrito, y destruir los datos biométricos cuando se cumple su finalidad, con plazos fijos." },
      { id: "cubi", name: "Texas (CUBI)", text: "Los identificadores biométricos deben destruirse en un plazo razonable y como máximo un año después de que termine la finalidad para la que se recogieron." },
      { id: "co", name: "Colorado", text: "Los datos biométricos deben eliminarse cuando se cumple su finalidad, con plazos máximos fijos y una revisión anual." },
      { id: "wa", name: "Washington", text: "Los identificadores biométricos no pueden guardarse más tiempo del razonablemente necesario para prestar el servicio." },
    ],
    ourChoice:
      "Lo único que podíamos elegir era la duración. Elegimos 12 meses: tiempo suficiente para cubrir el periodo en que los compradores buscan sus fotos, y dentro de los plazos que fijan estas leyes.",
    note: "Esta página explica cómo trata Photohawk los datos faciales. No es asesoramiento jurídico.",
  },
  faq: {
    title: "Preguntas",
    items: [
      { id: "photos", q: "¿Se eliminará alguna de mis fotos?", a: "No. Solo se eliminan los datos faciales de la búsqueda por selfie. Las fotos, las galerías, las ventas, los pedidos y las descargas no cambian." },
      { id: "action", q: "¿Tengo que hacer algo?", a: "No. Todo ocurre automáticamente. Si quieres, recuerda a los compradores que busquen poco después de cada evento." },
      { id: "longer", q: "¿Puedo conservar los datos faciales más tiempo o excluir mis galerías?", a: "No. El límite de 12 meses se aplica a todas las galerías de Photohawk, en todo el mundo, y no podemos hacer excepciones: un límite fijo que se aplica a todos es justo lo que piden estas leyes." },
      { id: "sooner", q: "¿Se pueden eliminar antes los datos faciales?", a: "Sí. Desactiva la búsqueda por selfie de una galería, o ciérrala, y sus datos faciales se eliminan 30 días después. Si la vuelves a activar dentro de esos 30 días, esa eliminación se cancela." },
      { id: "request", q: "¿Y si alguien pide que se eliminen sus datos faciales?", a: "Cualquiera puede pedirlo en cualquier momento, en la propia galería, desde la búsqueda por selfie, o escribiendo a support@photohawk.com. Sus datos faciales se eliminan en un plazo de 30 días, y también puedes hacerlo al momento desde los ajustes de búsqueda por selfie de la galería." },
      { id: "find", q: "¿Cómo encontrarán los compradores las fotos más antiguas?", a: "Con la búsqueda por dorsal o número, la búsqueda por hora o navegando por la galería, igual que hoy. Solo la búsqueda por selfie deja de encontrarlas." },
      { id: "selfies", q: "¿Se guardan los selfies de los compradores?", a: "No. Un selfie se usa solo para esa búsqueda y después se descarta, como hoy." },
      { id: "plan", q: "¿Depende de mi plan?", a: "No. Es igual en todos los planes y en todas las galerías." },
    ],
  },
  cta: {
    title: "Todos los detalles",
    body: "Nuestra política de datos faciales explica exactamente qué datos faciales guardamos, durante cuánto tiempo y cómo se eliminan. ¿Preguntas? Escríbenos y te responderá una persona.",
    policy: "Leer la política de datos faciales",
    contact: "Escribir a support@photohawk.com",
    allProducts: "Política de datos faciales de Photohawk Studio y la app Photohawk",
  },
  pauseLabel: "Pausar animación",
  playLabel: "Reproducir animación",
};

export default es;
